import {
  DEFAULT_NAMESPACE,
  type Entity,
  type EntityLink,
  parseEntityRef,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { isTechDocsAvailable } from '@backstage/plugin-techdocs';

import {
  groupEntityLinks,
  normalizeEntityLink,
  type EntityPageClassifiedLink,
  type EntityPageLinkGroups,
} from './entityPageLinkGroups';

export type EntityPageFieldSource = 'catalog' | 'convention' | 'missing';

export type EntityPageField<T> = {
  source: EntityPageFieldSource;
  value?: T;
};

export type EntityPageReference = {
  entityRef: string;
  kind: string;
  kindLabel: string;
  name: string;
  namespace: string;
  path: string;
  title: string;
};

export type EntityPageReferenceField = EntityPageField<string> & {
  reference?: EntityPageReference;
};

export type EntityPageRelationItem = EntityPageReference & {
  direction: 'upstream' | 'downstream';
  relation: string;
  source: 'catalog';
};

export type EntityPageRouteTarget = {
  available: boolean;
  path?: string;
  source: EntityPageFieldSource;
};

export type EntityPageApiDefinitionInfo = EntityPageRouteTarget & {
  definition?: string;
  rawLanguage?: string;
  relatedEntity?: EntityPageReference;
};

export type EntityPageBreadcrumb = {
  label: string;
  source: 'catalog';
  to?: string;
};

export type EntityPageModel = {
  about: {
    language: EntityPageField<string>;
    lifecycle: EntityPageField<string>;
    owner: EntityPageReferenceField;
    repository: EntityPageField<string>;
    system: EntityPageReferenceField;
    tags: string[];
    tier: EntityPageField<string>;
    type: EntityPageField<string>;
  };
  apiDefinition: EntityPageApiDefinitionInfo;
  documentation: {
    items: EntityPageClassifiedLink[];
    techDocs: EntityPageRouteTarget;
  };
  entityRef: string;
  hero: {
    breadcrumbs: EntityPageBreadcrumb[];
    description?: string;
    entityRef: string;
    kindLabel: string;
    lifecycle: EntityPageField<string>;
    owner: EntityPageReferenceField;
    starEnabled: boolean;
    tags: string[];
    title: string;
    type: EntityPageField<string>;
  };
  howToUse: {
    apiReference?: EntityPageClassifiedLink;
    apiTab: EntityPageRouteTarget;
    authentication: EntityPageField<string>;
    baseUrl: EntityPageField<string>;
    examples?: EntityPageClassifiedLink;
  };
  kind: string;
  links: {
    metadata: EntityPageLinkGroups;
    useful: EntityPageClassifiedLink[];
  };
  name: string;
  namespace: string;
  subcomponents: {
    items: EntityPageRelationItem[];
  };
  title: string;
  dependencies: {
    consumedBy: EntityPageRelationItem[];
    consumesApis: EntityPageRelationItem[];
    dependsOn: EntityPageRelationItem[];
    dependencyOf: EntityPageRelationItem[];
    downstream: EntityPageRelationItem[];
    partOf: EntityPageRelationItem[];
    providedBy: EntityPageRelationItem[];
    providesApis: EntityPageRelationItem[];
    upstream: EntityPageRelationItem[];
  };
};

const LANGUAGE_ANNOTATION_KEYS = [
  'meltwater.io/language',
  'backstage.io/language',
  'language',
];

const TIER_ANNOTATION_KEYS = ['meltwater.io/tier', 'backstage.io/tier', 'tier'];

const BASE_URL_ANNOTATION_KEYS = [
  'meltwater.io/base-url',
  'meltwater.io/baseUrl',
  'backstage.io/base-url',
  'backstage.io/baseUrl',
  'base-url',
];

const AUTHENTICATION_ANNOTATION_KEYS = [
  'meltwater.io/authentication',
  'backstage.io/authentication',
  'authentication',
  'auth',
];

const SPECIAL_CASE_LABELS: Record<string, string> = {
  api: 'API',
  apis: 'APIs',
  cd: 'CD',
  ci: 'CI',
  github: 'GitHub',
  graphql: 'GraphQL',
  kibana: 'Kibana',
  openapi: 'OpenAPI',
  pagerduty: 'PagerDuty',
  slo: 'SLO',
  techdocs: 'TechDocs',
};

const KIND_LABELS: Record<string, string> = {
  api: 'API',
  component: 'Component',
  domain: 'Domain',
  group: 'Group',
  location: 'Location',
  resource: 'Resource',
  system: 'System',
  template: 'Template',
  user: 'User',
};

const KIND_COLLECTION_LABELS: Record<string, string> = {
  api: 'APIs',
  component: 'Components',
  domain: 'Domains',
  group: 'Groups',
  location: 'Locations',
  resource: 'Resources',
  system: 'Systems',
  template: 'Templates',
  user: 'Users',
};

const getStringValue = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const humanizeToken = (token: string) => {
  const normalized = token.toLocaleLowerCase('en-US');
  if (SPECIAL_CASE_LABELS[normalized]) {
    return SPECIAL_CASE_LABELS[normalized];
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const humanizeMachineValue = (value: string) =>
  value
    .split(/[\s/_-]+/)
    .filter(Boolean)
    .map(humanizeToken)
    .join(' ');

const getKindLabel = (kind: string) =>
  KIND_LABELS[kind.toLocaleLowerCase('en-US')] ??
  humanizeMachineValue(kind.toLocaleLowerCase('en-US'));

const getKindCollectionLabel = (kind: string) =>
  KIND_COLLECTION_LABELS[kind.toLocaleLowerCase('en-US')] ??
  `${getKindLabel(kind)}s`;

const getEntityTitle = (entity: Entity) =>
  entity.metadata.title?.trim() || entity.metadata.name;

const getEntityNamespace = (entity: Pick<Entity, 'metadata'>) =>
  entity.metadata.namespace ?? DEFAULT_NAMESPACE;

const getEntityRoutePath = (
  entity: Pick<Entity, 'kind' | 'metadata'>,
  subpath?: string,
) => {
  const basePath = `/catalog/${getEntityNamespace(entity).toLocaleLowerCase(
    'en-US',
  )}/${entity.kind.toLocaleLowerCase('en-US')}/${entity.metadata.name}`;
  return subpath ? `${basePath}/${subpath.replace(/^\/+/, '')}` : basePath;
};

const getReferenceFromParsedRef = (parsedRef: {
  kind: string;
  name: string;
  namespace?: string;
}): EntityPageReference => {
  const namespace = parsedRef.namespace ?? DEFAULT_NAMESPACE;
  const kind = parsedRef.kind.toLocaleLowerCase('en-US');

  return {
    entityRef: stringifyEntityRef({
      kind: parsedRef.kind,
      name: parsedRef.name,
      namespace,
    }),
    kind,
    kindLabel: getKindLabel(kind),
    name: parsedRef.name,
    namespace,
    path: `/catalog/${namespace.toLocaleLowerCase('en-US')}/${kind}/${parsedRef.name}`,
    title: parsedRef.name,
  };
};

const getReferenceFromRef = (
  ref: string,
  options: {
    defaultKind?: string;
    defaultNamespace: string;
  },
): EntityPageReference | undefined => {
  try {
    const parsedRef = parseEntityRef(ref, {
      defaultKind: options.defaultKind,
      defaultNamespace: options.defaultNamespace,
    });
    return getReferenceFromParsedRef(parsedRef);
  } catch {
    return undefined;
  }
};

const getMetadataValue = (
  values: Record<string, unknown> | undefined,
  keys: string[],
) => keys.map(key => getStringValue(values?.[key])).find(Boolean);

const getConventionField = (
  entity: Entity,
  keys: string[],
): EntityPageField<string> => {
  const annotationValue = getMetadataValue(entity.metadata.annotations, keys);
  const labelValue = getMetadataValue(entity.metadata.labels, keys);
  const value = annotationValue ?? labelValue;

  return value ? { value, source: 'convention' } : { source: 'missing' };
};

const getCatalogField = (value: unknown): EntityPageField<string> => {
  const normalizedValue = getStringValue(value);
  return normalizedValue
    ? { value: normalizedValue, source: 'catalog' }
    : { source: 'missing' };
};

const getReferenceField = (
  rawRef: string | undefined,
  options: {
    defaultKind?: string;
    defaultNamespace: string;
    source: EntityPageFieldSource;
  },
): EntityPageReferenceField => {
  if (!rawRef) {
    return { source: 'missing' };
  }

  const reference = getReferenceFromRef(rawRef, {
    defaultKind: options.defaultKind,
    defaultNamespace: options.defaultNamespace,
  });

  return {
    reference,
    source: options.source,
    value: reference?.title ?? rawRef,
  };
};

const getRelationTargetRefs = (entity: Entity, relation: string) =>
  (entity.relations ?? [])
    .filter(item => item.type === relation)
    .map(item => item.targetRef);

const getUniqueRefs = (refs: string[]) => Array.from(new Set(refs));

const getRelationItems = ({
  defaultKind,
  defaultNamespace,
  direction,
  refs,
  relation,
}: {
  defaultKind?: string;
  defaultNamespace: string;
  direction: 'upstream' | 'downstream';
  refs: string[];
  relation: string;
}): EntityPageRelationItem[] =>
  getUniqueRefs(refs)
    .map(ref =>
      getReferenceFromRef(ref, {
        defaultKind,
        defaultNamespace,
      }),
    )
    .filter((reference): reference is EntityPageReference => Boolean(reference))
    .map(reference => ({
      ...reference,
      direction,
      relation,
      source: 'catalog',
    }));

export const getEntityRepositoryUrl = (
  entity: Entity,
): EntityPageField<string> => {
  const slug = getStringValue(
    entity.metadata.annotations?.['github.com/project-slug'],
  );

  if (!slug) {
    return { source: 'missing' };
  }

  const repositoryUrl = /^https?:\/\//.test(slug)
    ? slug
    : `https://github.com/${slug.replace(/^\/+/, '').replace(/\.git$/, '')}`;

  return {
    source: 'convention',
    value: repositoryUrl,
  };
};

export const getEntityTechDocsInfo = (
  entity: Entity,
): EntityPageRouteTarget => {
  const available = isTechDocsAvailable(entity);

  return {
    available,
    path: available ? getEntityRoutePath(entity, 'docs') : undefined,
    source: available ? 'catalog' : 'missing',
  };
};

export const getEntityApiDefinitionInfo = (
  entity: Entity,
): EntityPageApiDefinitionInfo => {
  const defaultNamespace = getEntityNamespace(entity);
  const entityKind = entity.kind.toLocaleLowerCase('en-US');
  const entitySpec = entity.spec ?? {};

  if (entityKind === 'api') {
    const definition = getStringValue(entitySpec.definition);
    return {
      available: Boolean(definition),
      definition,
      path: definition ? getEntityRoutePath(entity, 'definition') : undefined,
      rawLanguage: getStringValue(entitySpec.type)?.toLocaleLowerCase('en-US'),
      source: definition ? 'catalog' : 'missing',
    };
  }

  const relatedApiRef =
    getRelationTargetRefs(entity, RELATION_PROVIDES_API)[0] ??
    getRelationTargetRefs(entity, RELATION_CONSUMES_API)[0] ??
    (Array.isArray(entitySpec.providesApis)
      ? getStringValue(entitySpec.providesApis[0])
      : undefined) ??
    (Array.isArray(entitySpec.consumesApis)
      ? getStringValue(entitySpec.consumesApis[0])
      : undefined);

  if (!relatedApiRef) {
    return { available: false, source: 'missing' };
  }

  const relatedEntity = getReferenceFromRef(relatedApiRef, {
    defaultKind: 'API',
    defaultNamespace,
  });

  return relatedEntity
    ? {
        available: true,
        path: `${relatedEntity.path}/definition`,
        relatedEntity,
        source: 'catalog',
      }
    : {
        available: false,
        source: 'missing',
      };
};

const getOwnerField = (entity: Entity): EntityPageReferenceField => {
  const defaultNamespace = getEntityNamespace(entity);
  const entitySpec = entity.spec ?? {};
  const ownerRef =
    getStringValue(entitySpec.owner) ??
    getRelationTargetRefs(entity, RELATION_OWNED_BY)[0];

  return getReferenceField(ownerRef, {
    defaultKind: 'Group',
    defaultNamespace,
    source: ownerRef ? 'catalog' : 'missing',
  });
};

const getSystemField = (entity: Entity): EntityPageReferenceField => {
  const defaultNamespace = getEntityNamespace(entity);
  const entitySpec = entity.spec ?? {};
  const systemRef =
    getStringValue(entitySpec.system) ??
    getRelationTargetRefs(entity, RELATION_PART_OF).find(ref =>
      ref.toLocaleLowerCase('en-US').startsWith('system:'),
    );

  return getReferenceField(systemRef, {
    defaultKind: 'System',
    defaultNamespace,
    source: systemRef ? 'catalog' : 'missing',
  });
};

const getTypeField = (entity: Entity) =>
  getCatalogField((entity.spec ?? {}).type);

const getLifecycleField = (entity: Entity) =>
  getCatalogField((entity.spec ?? {}).lifecycle);

const getTags = (entity: Entity) => [...(entity.metadata.tags ?? [])];

const getApiTabInfo = (entity: Entity): EntityPageRouteTarget => {
  const entityKind = entity.kind.toLocaleLowerCase('en-US');
  const entityType = getStringValue(
    (entity.spec ?? {}).type,
  )?.toLocaleLowerCase('en-US');

  if (entityKind === 'api') {
    return {
      available: true,
      path: getEntityRoutePath(entity, 'definition'),
      source: 'catalog',
    };
  }

  if (entityKind === 'component' && entityType === 'service') {
    return {
      available: true,
      path: getEntityRoutePath(entity, 'api'),
      source: 'catalog',
    };
  }

  return {
    available: false,
    source: 'missing',
  };
};

const appendUniqueLinks = (
  links: EntityPageClassifiedLink[],
  extraLinks: Array<EntityPageClassifiedLink | undefined>,
) => {
  const combinedLinks = [...links];
  const seen = new Set(links.map(link => `${link.title}:${link.url}`));

  extraLinks.forEach(link => {
    if (!link) {
      return;
    }

    const key = `${link.title}:${link.url}`;
    if (!seen.has(key)) {
      seen.add(key);
      combinedLinks.push(link);
    }
  });

  return combinedLinks;
};

const getHeroBreadcrumbs = (entity: Entity): EntityPageBreadcrumb[] => {
  const entityKind = entity.kind.toLocaleLowerCase('en-US');
  const typeValue = getStringValue((entity.spec ?? {}).type);

  return [
    { label: 'Catalog', source: 'catalog', to: '/catalog' },
    {
      label: getKindCollectionLabel(entityKind),
      source: 'catalog',
    },
    {
      label: typeValue
        ? humanizeMachineValue(typeValue)
        : getKindLabel(entityKind),
      source: 'catalog',
    },
  ];
};

export const getEntityPageModel = (entity: Entity): EntityPageModel => {
  const title = getEntityTitle(entity);
  const entityRef = stringifyEntityRef(entity);
  const kind = entity.kind.toLocaleLowerCase('en-US');
  const namespace = getEntityNamespace(entity);
  const owner = getOwnerField(entity);
  const lifecycle = getLifecycleField(entity);
  const type = getTypeField(entity);
  const repository = getEntityRepositoryUrl(entity);
  const techDocs = getEntityTechDocsInfo(entity);
  const apiDefinition = getEntityApiDefinitionInfo(entity);
  const metadataLinks = groupEntityLinks(entity.metadata.links);

  const repositoryLink = repository.value
    ? normalizeEntityLink(
        {
          title: 'Source code',
          url: repository.value,
        } as EntityLink,
        'convention',
      )
    : undefined;

  const techDocsLink =
    techDocs.available && techDocs.path
      ? normalizeEntityLink(
          {
            icon: 'docs',
            title: 'TechDocs',
            url: techDocs.path,
          } as EntityLink,
          'catalog',
        )
      : undefined;

  const derivedApiReferenceLink =
    apiDefinition.available && apiDefinition.path
      ? normalizeEntityLink(
          {
            title: 'API reference',
            url: apiDefinition.path,
          } as EntityLink,
          apiDefinition.source === 'convention' ? 'convention' : 'catalog',
        )
      : undefined;

  const documentationItems = appendUniqueLinks(metadataLinks.documentation, [
    techDocsLink,
  ]);

  const usefulLinks = appendUniqueLinks(metadataLinks.useful, [repositoryLink]);
  const apiReference =
    metadataLinks.howToUse.find(
      link => link.classification === 'apiReference',
    ) ?? derivedApiReferenceLink;

  const examples = metadataLinks.howToUse.find(
    link => link.classification === 'examples',
  );

  const dependsOn = getRelationItems({
    defaultNamespace: namespace,
    direction: 'upstream',
    refs: getRelationTargetRefs(entity, RELATION_DEPENDS_ON),
    relation: RELATION_DEPENDS_ON,
  });

  const dependencyOf = getRelationItems({
    defaultNamespace: namespace,
    direction: 'downstream',
    refs: getRelationTargetRefs(entity, RELATION_DEPENDENCY_OF),
    relation: RELATION_DEPENDENCY_OF,
  });

  const partOf = getRelationItems({
    defaultNamespace: namespace,
    direction: 'upstream',
    refs: getRelationTargetRefs(entity, RELATION_PART_OF),
    relation: RELATION_PART_OF,
  });

  const providesApis = getRelationItems({
    defaultKind: 'API',
    defaultNamespace: namespace,
    direction: 'downstream',
    refs: [
      ...getRelationTargetRefs(entity, RELATION_PROVIDES_API),
      ...(
        (Array.isArray((entity.spec ?? {}).providesApis)
          ? (entity.spec ?? {}).providesApis
          : []) as unknown[]
      ).map(value => String(value)),
    ],
    relation: RELATION_PROVIDES_API,
  });

  const consumesApis = getRelationItems({
    defaultKind: 'API',
    defaultNamespace: namespace,
    direction: 'upstream',
    refs: [
      ...getRelationTargetRefs(entity, RELATION_CONSUMES_API),
      ...(
        (Array.isArray((entity.spec ?? {}).consumesApis)
          ? (entity.spec ?? {}).consumesApis
          : []) as unknown[]
      ).map(value => String(value)),
    ],
    relation: RELATION_CONSUMES_API,
  });

  const consumedBy = getRelationItems({
    defaultNamespace: namespace,
    direction: 'downstream',
    refs: getRelationTargetRefs(entity, RELATION_API_CONSUMED_BY),
    relation: RELATION_API_CONSUMED_BY,
  });

  const providedBy = getRelationItems({
    defaultNamespace: namespace,
    direction: 'upstream',
    refs: getRelationTargetRefs(entity, RELATION_API_PROVIDED_BY),
    relation: RELATION_API_PROVIDED_BY,
  });

  const subcomponents = getRelationItems({
    defaultNamespace: namespace,
    direction: 'downstream',
    refs: getRelationTargetRefs(entity, RELATION_HAS_PART),
    relation: RELATION_HAS_PART,
  });

  return {
    about: {
      language: getConventionField(entity, LANGUAGE_ANNOTATION_KEYS),
      lifecycle,
      owner,
      repository,
      system: getSystemField(entity),
      tags: getTags(entity),
      tier: getConventionField(entity, TIER_ANNOTATION_KEYS),
      type,
    },
    apiDefinition,
    dependencies: {
      consumedBy,
      consumesApis,
      dependsOn,
      dependencyOf,
      downstream: [
        ...dependencyOf,
        ...subcomponents,
        ...providesApis,
        ...consumedBy,
      ],
      partOf,
      providedBy,
      providesApis,
      upstream: [...partOf, ...dependsOn, ...consumesApis, ...providedBy],
    },
    documentation: {
      items: documentationItems,
      techDocs,
    },
    entityRef,
    hero: {
      breadcrumbs: getHeroBreadcrumbs(entity),
      description: getStringValue(entity.metadata.description),
      entityRef,
      kindLabel: getKindLabel(kind),
      lifecycle,
      owner,
      starEnabled: true,
      tags: getTags(entity),
      title,
      type,
    },
    howToUse: {
      apiReference,
      apiTab: getApiTabInfo(entity),
      authentication: getConventionField(
        entity,
        AUTHENTICATION_ANNOTATION_KEYS,
      ),
      baseUrl: getConventionField(entity, BASE_URL_ANNOTATION_KEYS),
      examples,
    },
    kind,
    links: {
      metadata: metadataLinks,
      useful: usefulLinks,
    },
    name: entity.metadata.name,
    namespace,
    subcomponents: {
      items: subcomponents,
    },
    title,
  };
};
